import React, { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { motion } from "framer-motion";

/**
 * Editable list of destinations where M.E. DMC has produced.
 * Coordinates are [longitude, latitude]. Add / remove freely.
 *
 * NOTE: Replace this seed list with the actual production history.
 */
const DMC_CITIES = [
  { name: "New York", country: "USA", coords: [-74.006, 40.7128], continent: "NA" },
  { name: "Los Angeles", country: "USA", coords: [-118.2437, 34.0522], continent: "NA" },
  { name: "Chicago", country: "USA", coords: [-87.6298, 41.8781], continent: "NA" },
  { name: "Miami", country: "USA", coords: [-80.1918, 25.7617], continent: "NA" },
  { name: "Boston", country: "USA", coords: [-71.0589, 42.3601], continent: "NA" },
  { name: "Washington, DC", country: "USA", coords: [-77.0369, 38.9072], continent: "NA" },
  { name: "Philadelphia", country: "USA", coords: [-75.1652, 39.9526], continent: "NA" },
  { name: "Atlanta", country: "USA", coords: [-84.388, 33.749], continent: "NA" },
  { name: "Dallas", country: "USA", coords: [-96.797, 32.7767], continent: "NA" },
  { name: "Las Vegas", country: "USA", coords: [-115.1398, 36.1699], continent: "NA" },
  { name: "San Diego", country: "USA", coords: [-117.1611, 32.7157], continent: "NA" },
  { name: "San Juan", country: "Puerto Rico", coords: [-66.1057, 18.4655], continent: "NA" },
  { name: "Mexico City", country: "Mexico", coords: [-99.1332, 19.4326], continent: "NA" },
  { name: "Cancún", country: "Mexico", coords: [-86.8515, 21.1619], continent: "NA" },
  { name: "London", country: "UK", coords: [-0.1278, 51.5074], continent: "EU" },
  { name: "Dublin", country: "Ireland", coords: [-6.2603, 53.3498], continent: "EU" },
  { name: "Paris", country: "France", coords: [2.3522, 48.8566], continent: "EU" },
  { name: "Barcelona", country: "Spain", coords: [2.1734, 41.3851], continent: "EU" },
  { name: "Amsterdam", country: "Netherlands", coords: [4.9041, 52.3676], continent: "EU" },
  { name: "Copenhagen", country: "Denmark", coords: [12.5683, 55.6761], continent: "EU" },
  { name: "Berlin", country: "Germany", coords: [13.405, 52.52], continent: "EU" },
  { name: "Zurich", country: "Switzerland", coords: [8.5417, 47.3769], continent: "EU" },
  { name: "Rome", country: "Italy", coords: [12.4964, 41.9028], continent: "EU" },
  { name: "Hyderabad", country: "India", coords: [78.4867, 17.385], continent: "AS" },
  { name: "Hong Kong", country: "Hong Kong", coords: [114.1694, 22.3193], continent: "AS" },
  { name: "Tokyo", country: "Japan", coords: [139.6917, 35.6895], continent: "AS" },
  { name: "Rio de Janeiro", country: "Brazil", coords: [-43.1729, -22.9068], continent: "SA" },
  { name: "Sydney", country: "Australia", coords: [151.2093, -33.8688], continent: "OC" },
];

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const CONTINENT_LABELS = {
  NA: "N. America",
  SA: "S. America",
  EU: "Europe",
  AS: "Asia",
  AF: "Africa",
  OC: "Oceania",
};

export default function DmcWorldMap() {
  const [hover, setHover] = useState(null);

  const stats = useMemo(() => {
    const cities = DMC_CITIES.length;
    const countries = new Set(DMC_CITIES.map((c) => c.country)).size;
    const continents = new Set(DMC_CITIES.map((c) => c.continent)).size;
    return { cities, countries, continents };
  }, []);

  return (
    <div className="relative">
      {/* Stats strip */}
      <div className="grid grid-cols-3 max-w-2xl mx-auto mb-8 md:mb-12 gap-4 md:gap-8">
        {[
          { k: stats.cities, v: "Destinations worked" },
          { k: stats.countries, v: "Countries covered" },
          { k: stats.continents, v: "Continents delivered" },
        ].map((s) => (
          <div
            key={s.v}
            className="text-center"
            data-testid={`dmc-map-stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-none bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent tabular-nums">
              {s.k}
              <span className="text-cyan-300">+</span>
            </div>
            <div className="mt-2 font-mono-brand text-[10px] md:text-[11px] uppercase tracking-[0.24em] text-white/60">
              {s.v}
            </div>
          </div>
        ))}
      </div>

      {/* Map */}
      <div
        className="relative rounded-3xl overflow-hidden bg-[#050A14] border border-white/8"
        style={{ boxShadow: "0 30px 80px -30px rgba(6,182,212,0.35)" }}
        data-testid="dmc-world-map"
      >
        {/* Ambient glow behind map */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-cyan-500/15 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-sky-500/15 blur-3xl" />
        </div>

        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: 170 }}
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#0B2545"
                  stroke="#1E3A8A"
                  strokeWidth={0.35}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "#123063", outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {DMC_CITIES.map((city) => (
            <Marker
              key={city.name}
              coordinates={city.coords}
              onMouseEnter={() => setHover(city)}
              onMouseLeave={() => setHover(null)}
              style={{ default: { cursor: "pointer" } }}
            >
              {/* Outer pulse */}
              <motion.circle
                r={6}
                fill="rgba(6, 182, 212, 0.35)"
                initial={{ scale: 0.6, opacity: 0.7 }}
                animate={{ scale: [0.6, 1.8, 0.6], opacity: [0.7, 0, 0.7] }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeOut",
                  delay: Math.random() * 2,
                }}
              />
              {/* Solid dot */}
              <circle
                r={2.4}
                fill="#22D3EE"
                stroke="#F5F7FA"
                strokeWidth={0.6}
                style={{
                  filter: "drop-shadow(0 0 4px rgba(34, 211, 238, 0.85))",
                }}
              />
            </Marker>
          ))}
        </ComposableMap>

        {/* Hover tooltip */}
        {hover && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-4 left-4 md:top-6 md:left-6 pointer-events-none z-10"
          >
            <div className="chip !bg-cyan-500/15 !border-cyan-300/40 !text-cyan-100 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-300" />
              <span className="font-editorial text-[15px] leading-none">
                {hover.name}
              </span>
              <span className="opacity-60 text-[11px]">· {hover.country}</span>
            </div>
          </motion.div>
        )}

        {/* Legend */}
        <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 pointer-events-none">
          <div className="flex items-center gap-2 rounded-full bg-white/5 border border-white/10 backdrop-blur px-3 py-1.5">
            <span className="relative w-2 h-2 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.85)]" />
            <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-white/70">
              DMC destination
            </span>
          </div>
        </div>
      </div>

      {/* City chip cloud below the map */}
      <div className="mt-8 md:mt-10 flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
        {DMC_CITIES.map((c) => (
          <span
            key={c.name}
            className="text-[11px] md:text-[12px] font-mono-brand uppercase tracking-[0.16em] text-white/60 border border-white/10 rounded-full px-3 py-1 bg-white/[0.03] hover:text-cyan-300 hover:border-cyan-300/30 transition-colors"
            data-testid={`dmc-city-chip-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {c.name}
          </span>
        ))}
      </div>
    </div>
  );
}
